import {Color} from '@benchling/nucleus-ui';
import {createStyles, Text} from '@mantine/core';
import React from 'react';
import {Node, NodeProps} from 'reactflow';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {FONT_PROXIMA_11, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';

export const PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE = 'procedureRunDesignFlowchartColumnNode';

export const FALLBACK_COLUMN_NODE_WIDTH = COMMON_NODE_WIDTH;
export const FALLBACK_COLUMN_NODE_HEIGHT = 52; // best-effort static fallback; see TODO (BNCH-156222)
export interface ProcedureRunDesignFlowchartColumnNodeData {
  methodName: string;
  factorsSet: number;
  methodId: string;
  procedureType: ProcedureType;
  isActiveMethod: boolean;
  isHighlighted: boolean;
  isUpstreamOfActiveMethod: boolean;
}

interface ColumnHeaderProps {
  methodName: string;
  factorsSet: number;
  isHighlighted: boolean;
  isUpstreamOfActiveMethod: boolean;
}

export function createProcedureRunDesignFlowchartColumnNode({
  methodId,
  methodName,
  factorsSet,
  procedureType,
  isActiveMethod,
  isHighlighted,
  isUpstreamOfActiveMethod,
}: {
  methodId: string;
  methodName: string;
  factorsSet: number;
  procedureType: ProcedureType;
  isActiveMethod: boolean;
  isHighlighted: boolean;
  isUpstreamOfActiveMethod: boolean;
}): Node<ProcedureRunDesignFlowchartColumnNodeData> {
  return {
    id: `column-${methodId}`,
    type: PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE,
    style: {
      width: FALLBACK_COLUMN_NODE_WIDTH,
      height: FALLBACK_COLUMN_NODE_HEIGHT,
    },
    position: {
      x: 0,
      y: 0,
    },
    data: {
      methodName,
      factorsSet,
      methodId,
      procedureType,
      isActiveMethod,
      isHighlighted,
      isUpstreamOfActiveMethod,
    },
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
  };
}

export function isProcedureRunDesignFlowchartColumnNode(
  node: Node
): node is Node<ProcedureRunDesignFlowchartColumnNodeData> {
  return node.type === PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE;
}

function getFactorsSetText(factorsSet: number): string {
  if (factorsSet === 0) {
    return 'No factors set';
  }
  return `${factorsSet} ${factorsSet === 1 ? 'factor' : 'factors'}`;
}

function ProcedureRunDesignFlowchartColumnHeader({
  methodName,
  factorsSet,
  isHighlighted,
  isUpstreamOfActiveMethod,
}: ColumnHeaderProps): JSX.Element {
  const {classes, cx} = useHeaderStyles();
  const factorsSetText = getFactorsSetText(factorsSet);
  return (
    <div className={classes.container}>
      <div className={classes.methodNameRow}>
        <Text
          className={cx(
            classes.methodName,
            isHighlighted && classes.methodNameHighlighted,
            isUpstreamOfActiveMethod && classes.methodNameLocked
          )}
          title={methodName}
        >
          {methodName}
        </Text>
        {isUpstreamOfActiveMethod ? (
          <BenchlingIcon icon="lock" className={classes.lockIcon} altText={`${methodName} is locked`} />
        ) : null}
      </div>
      <Chip
        className={cx(
          classes.chip,
          !isUpstreamOfActiveMethod && classes.chipNotLocked,
          isUpstreamOfActiveMethod && classes.chipLocked
        )}
        size={ChipSize.SMALL}
        isReadonly={true}
        text={factorsSetText}
      />
    </div>
  );
}

export default function ProcedureRunDesignFlowchartColumnNode(
  props: NodeProps<ProcedureRunDesignFlowchartColumnNodeData>
): JSX.Element {
  const {methodName, factorsSet, procedureType, isActiveMethod, isHighlighted, isUpstreamOfActiveMethod} =
    props.data;
  const methodString = getProcedureUIString(ProceduresStringKeys.METHOD, procedureType, {
    stringCase: StringCase.LOWER,
  });
  const {classes, cx} = useColumnNodeStyles();
  return (
    <div className={cx(classes.container, isActiveMethod && classes.activeMethodContainer)}>
      {isActiveMethod ? <div className={classes.activeMethodOutline} /> : null}
      {isActiveMethod ? <div className={classes.activeMethodLabel}>{`Active ${methodString}`}</div> : null}
      <ProcedureRunDesignFlowchartColumnHeader
        methodName={methodName}
        factorsSet={factorsSet}
        isHighlighted={isHighlighted}
        isUpstreamOfActiveMethod={isUpstreamOfActiveMethod}
      />
    </div>
  );
}

const useColumnNodeStyles = createStyles((theme) => {
  const spacingSm = theme.spacing.sm;
  const negativeSpacingSm = typeof spacingSm === 'number' ? -spacingSm : `calc(${spacingSm} * -1)`;

  return {
    container: {
      width: '100%',
      height: '100%',
      cursor: 'pointer',
      position: 'relative',
    },
    activeMethodContainer: {
      // Keep room for the label and render an outline that extends horizontally.
      overflow: 'visible',
    },
    activeMethodOutline: {
      position: 'absolute',
      top: negativeSpacingSm,
      bottom: negativeSpacingSm,
      left: negativeSpacingSm,
      right: negativeSpacingSm,
      border: `1px solid ${theme.colors.blue[7]}`,
      borderRadius: theme.radius.sm,
      pointerEvents: 'none',
    },
    activeMethodLabel: {
      position: 'absolute',
      top: negativeSpacingSm,
      left: negativeSpacingSm,
      transform: `translateY(calc(-100% - ${theme.spacing.xs}))`,
      // Match the blue title pill styling in GroupFlowchartNode.
      ...FONT_PROXIMA_11,
      fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
      padding: '2px 6px',
      borderRadius: '4px',
      whiteSpace: 'nowrap',
      color: Color.WHITE,
      backgroundColor: theme.colors.blue[7],
    },
  };
});

const useHeaderStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  methodNameRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    width: '100%',
    minWidth: 0,
  },
  methodName: {
    fontWeight: 600,
    fontSize: theme.fontSizes.md,
    lineHeight: 1,
    color: Color.GRAY_900,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: 'auto',
    maxWidth: '100%',
    flex: '0 1 auto',
    minWidth: 0,
  },
  methodNameHighlighted: {
    color: theme.colors.blue[6],
  },
  methodNameLocked: {
    color: Color.GRAY_400,
  },
  lockIcon: {
    color: Color.GRAY_400,
    fontSize: theme.fontSizes.sm,
    flexShrink: 0,
  },
  chip: {
    '&.chip, &.chip:hover, &.chip:active': {
      cursor: 'default',
      margin: 0,
      padding: 3,
    },
  },
  chipNotLocked: {
    '&.chip, &.chip:hover, &.chip:active': {
      backgroundColor: Color.BLUE_100,
      borderColor: Color.BLUE_100,
      color: Color.BLUE_400,
    },
    '&.chip .chip-text, &.chip .chip-text-secondaryText': {
      fontWeight: 600,
    },
  },
  chipLocked: {
    '&.chip, &.chip:hover, &.chip:active': {
      backgroundColor: Color.GRAY_100,
      borderColor: Color.GRAY_100,
    },
    '&.chip .chip-text, &.chip .chip-text-secondaryText': {
      color: Color.GRAY_400,
    },
  },
}));
